import React from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Button,
  Avatar,
  styled,
  Pagination,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { formattedAmountWithoutAed } from "../../../utils/CommonFunction";
import { renderCurrencyIconColored } from "../../../utils/CurrencyUtils";
import moment from "moment";
import { statusStyleArray } from "../config";
import {
  isDesignScope,
  ScopeTypeBadge,
  getBidAmount,
} from "./RFQDetails";

const ActiveCard = styled(Card)(({ theme }) => ({
  borderRadius: "12px",
  boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)",
  cursor: "pointer",
  transition: "transform 0.2s ease-in-out",
  overflow: "hidden",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0px 8px 20px rgba(0, 0, 0, 0.15)",
  },
}));

const ProjectTypePill = styled(Box)({
  position: "absolute",
  top: "12px",
  left: "12px",
  padding: "4px 10px",
  borderRadius: "999px",
  backgroundColor: "#FFFFFF",
  color: "#0D0D0E",
  fontSize: "12px",
  fontWeight: 600,
  fontFamily: "Inter Tight",
  textTransform: "capitalize",
  boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
  letterSpacing: 0,
});

const ProjectName = styled(Typography)({
  fontSize: "16px",
  fontWeight: 600,
  color: "#0D0D0E",
  marginBottom: "2px",
  fontFamily: "Inter Tight",
  letterSpacing: 0,
});

const ProjectSummary = styled(Typography)({
  fontSize: "13px",
  color: "#949DAA",
  fontFamily: "Inter Tight",
  letterSpacing: 0,
});

const ActionButton = styled(Button)({
  backgroundColor: "#4D6BF6",
  color: "#FFFFFF",
  borderRadius: "8px",
  fontSize: "14px",
  fontWeight: 600,
  textTransform: "none",
  width: "100%",
  padding: "8px 16px",
  fontFamily: "Inter Tight",
  letterSpacing: 0,
  boxShadow: "none",
  "&:hover": {
    backgroundColor: "#3B5BDB",
    boxShadow: "none",
  },
});

const SectionTitle = styled(Typography)({
  fontSize: "18px",
  fontWeight: 600,
  color: "#0D0D0E",
  marginBottom: "16px",
  display: "flex",
  alignItems: "center",
  gap: "8px",
  fontFamily: "Inter Tight",
  letterSpacing: 0,
});

const CountBadge = styled(Box)({
  backgroundColor: "#E5E7EB",
  color: "#6B7280",
  borderRadius: "50%",
  width: "24px",
  height: "24px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "12px",
  fontWeight: 600,
});

const SubmittedCard = styled(Card)({
  margin: "0 !important",
  borderRadius: "12px",
  boxShadow: "none",
  border: "1px solid #E5E7EB",
  cursor: "pointer",
  background: "#FFFFFF",
});

export default function MobileRFQCards({
  mode = "active",
  // Active Requests
  activeRequests = [],
  activeLoading = false,
  activePagination,
  activePage = 1,
  onActivePageChange,
  // Submitted (status=proposal)
  submitted = [],
  submittedLoading = false,
  submittedPagination,
  submittedPage = 1,
  onSubmittedPageChange,
  // Submitted (status=awaiting-project-pricing)
  pricing = [],
  pricingLoading = false,
  pricingPagination,
  pricingPage = 1,
  onPricingPageChange,
  // Awarded (status=approved-by-contractor)
  awarded = [],
  awardedLoading = false,
  awardedPagination,
  awardedPage = 1,
  onAwardedPageChange,
  // Archived
  archived = [],
  archivedLoading = false,
  archivedPagination,
  archivedPage = 1,
  onArchivedPageChange,
  // Layout
  isMobile,
  isTablet,
}) {
  const navigate = useNavigate();

  const getStatusDisplayText = (status) => {
    if (!status) return "-";
    return status
      .replace(/[-_]/g, " ")
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // Design scopes route to their own bid flow (deliverables/payments).
  const getPricingPath = (item) =>
    isDesignScope(item)
      ? `/rfq/design-pricing/${item.scope_id}/${item.project_id}`
      : `/rfq/pricing/${item.scope_id}/${item.project_id}`;

  const handleCardClick = (item) => {
    navigate(getPricingPath(item));
  };

  const handleAddPricing = (item) => {
    navigate(getPricingPath(item));
  };

  // Renders a single Submitted / Awarded / Archived card. All three
  // sections use the same card shape so the renderer is shared.
  const renderBidCard = (item) => {
    const amount = getBidAmount(item);
    const status = item.status || item.proposal_status;
    const datesText =
      item?.start_date && item?.end_date
        ? `${moment(item.start_date, "MMMM DD, YYYY").format(
            "DD MMM"
          )} - ${moment(item.end_date, "MMMM DD, YYYY").format(
            "DD MMM, YYYY"
          )}`
        : item?.start_date
        ? moment(item.start_date, "MMMM DD, YYYY").format("DD MMM, YYYY")
        : item?.end_date
        ? moment(item.end_date, "MMMM DD, YYYY").format("DD MMM, YYYY")
        : "-";
    return (
      <SubmittedCard
        key={item.proposal_id || item.id}
        onClick={() => handleCardClick(item)}
      >
        <CardContent sx={{ padding: "14px" }}>
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              gap: "10px",
            }}
          >
            {/* Title row + project type */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <Typography
                sx={{
                  fontSize: "15px",
                  fontWeight: 600,
                  color: "#0D0D0E",
                  maxWidth: "65%",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  fontFamily: "Inter Tight",
                  letterSpacing: 0,
                }}
              >
                {item.name || "-"}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: "6px", flexShrink: 0 }}>
                {item.project_type && (
                  <Typography
                    sx={{
                      fontSize: "13px",
                      color: "#6B7280",
                      fontFamily: "Inter Tight",
                      letterSpacing: 0,
                    }}
                  >
                    {item.project_type}
                  </Typography>
                )}
                <ScopeTypeBadge item={item} />
              </Box>
            </Box>

            {/* Customer (left) + Status (right) */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "8px",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
                <Avatar
                  sx={{
                    width: "30px",
                    height: "30px",
                    fontSize: "12px",
                    marginRight: "8px",
                    backgroundColor: "#E5E7EB",
                    flexShrink: 0,
                  }}
                  src={item?.user_data?.profile_url}
                >
                  {item?.user_data?.username?.charAt(0)?.toUpperCase() ||
                    item?.username?.charAt(0)?.toUpperCase() ||
                    "U"}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: "11px",
                      color: "#949DAA",
                      fontFamily: "Inter Tight",
                      letterSpacing: 0,
                    }}
                  >
                    Customer
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "13px",
                      fontWeight: 500,
                      color: "#0D0D0E",
                      fontFamily: "Inter Tight",
                      letterSpacing: 0,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {item?.user_data?.username || item?.username || "-"}
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexShrink: 0,
                  paddingLeft: "8px",
                }}
              >
                <Box
                  sx={{
                    width: "12px",
                    height: "12px",
                    borderRadius: "50%",
                    backgroundColor:
                      statusStyleArray.find((style) => style.status === status)
                        ?.backgroundColor || "#E5E5E5",
                    marginRight: "6px",
                    border: "2px solid #e5e5e5",
                  }}
                />
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: 500,
                    color: "#0D0D0E",
                    fontFamily: "Inter Tight",
                    letterSpacing: 0,
                  }}
                >
                  {getStatusDisplayText(status)}
                </Typography>
              </Box>
            </Box>

            {/* Dates (left) + Amount (right) */}
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "space-between",
                gap: "8px",
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: "11px",
                    color: "#949DAA",
                    fontWeight: 500,
                    fontFamily: "Inter Tight",
                    letterSpacing: 0,
                  }}
                >
                  Start/ End Date
                </Typography>
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: 500,
                    color: "#0D0D0E",
                    fontFamily: "Inter Tight",
                    letterSpacing: 0,
                    whiteSpace: "nowrap",
                  }}
                >
                  {datesText}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-end",
                  gap: "2px",
                  flexShrink: 0,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "11px",
                    color: "#949DAA",
                    fontWeight: 500,
                    fontFamily: "Inter Tight",
                    letterSpacing: 0,
                  }}
                >
                  Amount
                </Typography>
                <Typography
                  component="span"
                  sx={{
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#0D0D0E",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                    fontFamily: "Inter Tight",
                    letterSpacing: 0,
                  }}
                >
                  {amount ? (
                    <>
                      {renderCurrencyIconColored("black-small")}
                      {formattedAmountWithoutAed(amount)}
                    </>
                  ) : (
                    "-"
                  )}
                </Typography>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </SubmittedCard>
    );
  };

  // Renders a section pagination footer (inline, not sticky) so each
  // section can paginate independently.
  const renderSectionPagination = (paginationObj, currentPage, onChange) => {
    if (!paginationObj || (paginationObj.totalPages || 1) <= 1) return null;
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          padding: "12px 0",
        }}
      >
        <Pagination
          count={paginationObj.totalPages || 1}
          page={currentPage}
          onChange={(event, value) => onChange(value)}
          color="primary"
          size="small"
        />
      </Box>
    );
  };

  const renderActiveRequestCard = (item) => (
    <ActiveCard key={item.id} onClick={() => handleAddPricing(item)}>
      <Box sx={{ position: "relative" }}>
        <CardMedia
          component="img"
          height="130"
          image={item.project_image?.[0]?.image || "/api/placeholder/300/130"}
          alt={item.name}
          sx={{
            backgroundColor: "#F2F4F7",
            objectFit: "cover",
          }}
        />

        {/* Project type (left) + scope type (right) on one row */}
        <Box
          sx={{
            position: "absolute",
            top: "12px",
            left: "12px",
            right: "12px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "8px",
          }}
        >
          {item.project_type ? (
            <ProjectTypePill sx={{ position: "static" }}>
              {item.project_type}
            </ProjectTypePill>
          ) : (
            <span />
          )}
          <ScopeTypeBadge item={item} />
        </Box>
      </Box>

      <CardContent sx={{ padding: "12px" }}>
        <Box sx={{ display: "flex", alignItems: "center", marginBottom: "10px" }}>
          <Avatar
            sx={{
              width: "30px",
              height: "30px",
              marginRight: "8px",
              backgroundColor: "#E5E7EB",
              fontSize: "12px",
            }}
          >
            {item.username?.charAt(0)?.toUpperCase() || "M"}
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <ProjectName noWrap sx={{ fontSize: "14px" }}>
              {item.name}
            </ProjectName>
            <ProjectSummary noWrap sx={{ fontSize: "12px" }}>
              {item.username || item.user_data?.username || "-"}
            </ProjectSummary>
          </Box>
        </Box>

        <ActionButton
          sx={{ padding: "6px 14px", fontSize: "13px" }}
          onClick={(e) => {
            e.stopPropagation();
            handleAddPricing(item);
          }}
        >
          Add Pricing
        </ActionButton>
      </CardContent>
    </ActiveCard>
  );

  // Per-tab loading flag drives the spinner inside the active section.
  const sectionLoading =
    mode === "active"
      ? activeLoading
      : mode === "submitted"
      ? submittedLoading
      : mode === "pricing"
      ? pricingLoading
      : mode === "awarded"
      ? awardedLoading
      : archivedLoading;

  // Active grid: 1 column on mobile, 2 columns on tablet
  const activeGridTemplate = isTablet
    ? "repeat(2, minmax(0, 1fr))"
    : "1fr";

  const renderLoading = () => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: 200,
      }}
    >
      <Typography sx={{ color: "#6B7280", fontFamily: "Inter Tight" }}>
        Loading…
      </Typography>
    </Box>
  );

  const renderEmpty = (text) => (
    <Typography
      variant="body1"
      sx={{ textAlign: "center", marginTop: 4, color: "#6B7280" }}
    >
      {text}
    </Typography>
  );

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      {/* Inner padding so cards don't kiss the screen edges, and so the
          scroll thumb has breathing room on mobile/tablet. */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          padding: "8px 12px 16px 12px",
        }}
      >
        {sectionLoading ? (
          renderLoading()
        ) : mode === "active" ? (
          activeRequests.length === 0 ? (
            renderEmpty("No active requests")
          ) : (
            <Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: activeGridTemplate,
                  gap: "12px",
                }}
              >
                {activeRequests.map(renderActiveRequestCard)}
              </Box>
              {renderSectionPagination(
                activePagination,
                activePage,
                onActivePageChange
              )}
            </Box>
          )
        ) : mode === "submitted" ? (
          submitted.length === 0 ? (
            renderEmpty("No submitted bids yet")
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              {submitted.map(renderBidCard)}
              {renderSectionPagination(
                submittedPagination,
                submittedPage,
                onSubmittedPageChange
              )}
            </Box>
          )
        ) : mode === "pricing" ? (
          pricing.length === 0 ? (
            renderEmpty("No submitted bids yet")
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              {pricing.map(renderBidCard)}
              {renderSectionPagination(
                pricingPagination,
                pricingPage,
                onPricingPageChange
              )}
            </Box>
          )
        ) : mode === "awarded" ? (
          awarded.length === 0 ? (
            renderEmpty("No awarded bids yet")
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              {awarded.map(renderBidCard)}
              {renderSectionPagination(
                awardedPagination,
                awardedPage,
                onAwardedPageChange
              )}
            </Box>
          )
        ) : (
          // mode === "archived"
          archived.length === 0 ? (
            renderEmpty("No archived bids")
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
              {archived.map(renderBidCard)}
              {renderSectionPagination(
                archivedPagination,
                archivedPage,
                onArchivedPageChange
              )}
            </Box>
          )
        )}
      </Box>
    </Box>
  );
}
